import L from "leaflet";
import IconMapPersonLocation from "../../assets/IconsMap/ic-map-personal-check.svg";
import { useCallback, useRef } from "react";
import {GetUserById} from "../../reduxToolkit/EmployeePayRoll/EmployeePayRollApi";


/**
 * Provides reusable map logic for handling company location markers and employee information tooltips.
 * This utility sets up Leaflet map markers with hover-based user data fetching.
 *
 * @component
 * @function CommonMapApp
 * @param {Object} props - Function parameters.
 * @param {Function} props.t - Translation function from i18n.
 * @param {Array<Object>} props.locations - List of location objects containing `latitude`, `longitude`, and `userId`.
 * @param {Function} props.dispatch - Redux dispatch function used for API calls.
 *
 *
 * @description
 * This component defines:
 * - A custom Leaflet icon for company markers.
 * - Marker hover behavior that fetches and displays user data dynamically.
 * - Tooltip caching and hover delay for better UX and API performance.
 *
 * @createdAt 2025-10-25
 * @autor Arnulfo Ferro Zapata
 * @email azapata@ibl.mx
 * @jobTitle Full Stack Developer
 */

export const CommonMapApp =({  t, locations, dispatch})=>{

    const companyIcon = new L.Icon({
        iconUrl: IconMapPersonLocation,
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32]
    });

    const hoverTimers = useRef({});
    const markerDataCache = useRef({});

    const marker = locations.map((item) => ({
        position: [item?.latitude, item?.longitude],
        ...item
    }));

    const handleMarkerHover = useCallback((marker, leafletMarker) => {
        if (!marker.userId) return;
        const markerId = marker.userId;

        const ensureTooltip = (content = '') => {
            if (!leafletMarker.getTooltip()) {
                leafletMarker.bindTooltip(content, {
                    permanent: false,
                    direction: 'top',
                    className: 'p-2 ibl-font-w-600 font-color-general-950 border-radius-4px'
                });
            } else {
                leafletMarker.setTooltipContent(content);
            }
            leafletMarker.openTooltip();
        };

        if (markerDataCache.current[markerId]) {
            ensureTooltip(`${t('employee')}: ${markerDataCache.current[markerId]}`);
            ensureTooltip(
                `${t('employee')}: ${markerDataCache.current[markerId].fullName}
                            <br> ${t('email')}: ${markerDataCache.current[markerId].email}
                            <br> ${t('phone_number')}: ${markerDataCache.current[markerId].phoneNumber} `);
            return;
        }
        if (hoverTimers.current[markerId]) clearTimeout(hoverTimers.current[markerId]);

        const timer = setTimeout(async () => {
            ensureTooltip(`<span>${t('loading_information')}</span>`);

            try {
                const { payload: { data: { data, success } } } =
                    await dispatch(GetUserById({ params: { id: markerId } }));

                if (success) {
                    markerDataCache.current[markerId] ={fullName: data.fullName, email : data.email, phoneNumber: data.phoneNumber}
                    ensureTooltip(
                        `${t('employee')}: ${data.fullName}
                            <br> ${t('email')}: ${data.email}
                            <br> ${t('phone_number')}: ${data.phoneNumber} `);
                }
            } catch (err) {
                ensureTooltip(`${t('something_went_wrong')}`);
            }
        }, 500);

        hoverTimers.current[markerId] = timer;

        const handleMouseOut = () => {
            clearTimeout(hoverTimers.current[markerId]);
            delete hoverTimers.current[markerId];
            leafletMarker.closeTooltip();
            leafletMarker.off('mouseout', handleMouseOut);
        };

        leafletMarker.off('mouseout', handleMouseOut);
        leafletMarker.on('mouseout', handleMouseOut);
    }, [dispatch, t]);

    return {
        companyIcon,
        marker,
        handleMarkerHover,
    }
}
